import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    CreateOrderFromMedSheet,
    OrderSourceEnum,
    OrderStatusEmrEnum,
    OrderTemplate,
} from '../../models/PatientModels';
import RoundedButton from '../RoundedButton';
import {
    OrderContainer,
    OrderFooter,
    OrderItem,
    OrderItemInline,
    OrderNotFound,
    OrderTitle,
} from './OrderCard';
import { OrderTab, OrderTabGrid } from './OrderTab';
import {
    useCreateOrderFromMedSheetMutation,
    useGetLatestMedPullQuery,
    useGetOrderTemplatesQuery,
} from '../../features/patient/patientService';
import { useEffect, useState } from 'react';
import useOrderToast from '../../hooks/useOrderToast';
import moment from 'moment';
import { EditOutlined, SyncOutlined } from '@ant-design/icons';
import ConfirmationModal from './ConfirmationModal';
import { Utility } from '../../utilities/Utility';
import { Button, Spin } from 'antd';
import { MedPullSyncStatus } from '../../models/MedOrders';

const MedSheets = ({
    medSheet,
    handleSubmit,
    handleEdit
}: {
    medSheet: OrderTemplate;
    handleSubmit: () => void;
    handleEdit: () => void;
}) => {
    return (
        <OrderContainer title={
            <OrderTitle>
                <div className="d-inline-block me-3 overflow-hidden text-truncate text-wrap" title={medSheet.drugName}>
                    <span>{medSheet.drugName}</span>
                </div>
                <EditOutlined
                    onClick={handleEdit}
                    rev={undefined}
                    style={{ color: 'rgb(88, 89, 91)' }}
                />
            </OrderTitle>

        }>
            <OrderItemInline
                label="Source:"
                value={OrderSourceEnum[medSheet.source].toUpperCase()}
                className="text-medium-blue"
            />
            <OrderItem
                label="Drug Format"
                value={medSheet.drugFormat ?? 'N/A'}
            />
            <OrderFooter>
                {
                    <RoundedButton
                        onClick={handleSubmit}
                        className={
                            'fw-semibold bg-light-blue text-white fw-semibold'
                        }
                    >
                        Review & Submit
                    </RoundedButton>
                }
            </OrderFooter>
        </OrderContainer>
    );
};

const MedSheetsTab = (
    {
        medSheets, 
        isLoading, 
        refetchMedsheets
    }
    :
    {
        medSheets: OrderTemplate[] | undefined, 
        isLoading: boolean, 
        refetchMedsheets: () => void
    }
) => {
    const { id: patientId = '' } = useParams();
    const navigate = useNavigate();

    const { toast } = useOrderToast();

    const [createOrderFromMedSheet, { isLoading: isCreatingOrder, error }] =
        useCreateOrderFromMedSheetMutation();

    const [matchDrugFromMedSheetForEdit, { isLoading: isMatchingDrugForEdit }] =
        useCreateOrderFromMedSheetMutation();

    useEffect(() => {
        if (!error) return;
        setIsErrorModalOpen(true);
    }, [error]);

    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currMedsheetId, setCurrMedsheetId] = useState("");

    const handleSubmit = async ({
        medSheetId,
        patientId,
    }: CreateOrderFromMedSheet) => {
        setCurrMedsheetId(medSheetId);
        const response = await createOrderFromMedSheet({
            medSheetId,
            patientId,
        });

        if (typeof response === 'object' && 'data' in response) {
            const orderId = response.data.id;
            navigate(`/patient/${patientId}/pending-order/${orderId}`);
        }
    };

    const handleEdit = async ({
        medSheetId,
        patientId,
    }: CreateOrderFromMedSheet) => {

        // Use the same endpoint to find and save the matched drug on ScriptSure's end to pre-select the drug in edit if exists.
        await matchDrugFromMedSheetForEdit({
            medSheetId,
            patientId,
        });

        navigate(`${medSheetId}`);
    };

    const { data: latestSync, isLoading: isLatestMedPullLoading, refetch: refetchLatestSync } = useGetLatestMedPullQuery({patientId: patientId});

    const formattedDate = latestSync?.latestSyncDate
        ? Utility.getFormattedDateTime(latestSync?.latestSyncDate)
        : 'N/A';

    useEffect(() => {
        refetchMedsheets()
    }, [latestSync?.latestSyncDate])

    return (
        <>
        <OrderTab isLoading={isLoading || isCreatingOrder || isMatchingDrugForEdit}>
            <div className='d-flex flex-row w-100 justify-content-between'>
                <div className='d-flex flex-row align-items-center'>
                    <span>
                        <b>Note:</b> The CURRENT TREATMENT / MEDICATION / DME LIST was last updated on {formattedDate}, 
                        with Status: {latestSync?.status != undefined ? MedPullSyncStatus[latestSync?.status] : "N/A"}
                    </span>
                </div>
            </div>
            <OrderTabGrid>
                {medSheets &&
                    medSheets.map((medSheet) => (
                        <MedSheets
                            key={medSheet.id}
                            medSheet={medSheet}
                            handleSubmit={() => {
                                handleSubmit({
                                    medSheetId: medSheet.id,
                                    patientId,
                                });
                            }}
                            handleEdit={() => {
                                handleEdit({
                                    medSheetId: medSheet.id,
                                    patientId,
                                });
                            }}
                        />
                    ))}
            </OrderTabGrid>
            {!isLoading && (!medSheets || medSheets.length === 0) && (
                <OrderNotFound>
                    There are no med sheets to be displayed.
                </OrderNotFound>
            )}
        </OrderTab>
        <ConfirmationModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title='Successful!'
            message='The medication(s) were successfully sent'
            primaryButtonText='Review Medication'
            secondaryButtonText='View Medication Queue'
            onPrimaryButtonLinkTo={`/patient/${patientId}/approve-medications/view`}
            onSecondaryButtonLinkTo={`/patient/${patientId}/add-new-medication`}
        />

        <ConfirmationModal
            isOpen={isErrorModalOpen}
            onClose={() => setIsErrorModalOpen(false)}
            isSuccess={false}
            title='Failed'
            message='Medication not found in Scriptsure. 
            You can review and correct the Med Sheet details in the EMR, 
            or proceed by updating the Med Sheet via NaviScript. Updates made via NaviScript will not modify the original
            Med Sheet pulled from the EMR.'
            primaryButtonText='Update Med Order via NaviScript'
            secondaryButtonText='View Med Sheets'
            onPrimaryButtonLinkToClick={()=> {
                setIsErrorModalOpen(false);
                navigate(`${currMedsheetId}`);
            }}
            onSecondaryButtonLinkClick={()=>setIsErrorModalOpen(false)}
        />
        </>
    );
};

export default MedSheetsTab;
